import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { 
  ArrowLeft, 
  Plus,
  Calendar,
  MapPin,
  Heart,
  AlertTriangle,
  CheckCircle2,
  Smile,
  Meh,
  Frown,
  TrendingUp,
  Edit,
  Trash2,
  User,
  Clock,
  Filter,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

interface DateEntry {
  id: string;
  personName: string;
  date: string;
  location: string;
  activityType: string;
  duration: number; // in hours
  emotionalState: 'great' | 'good' | 'neutral' | 'mixed' | 'disappointed';
  connectionLevel: number; // 1-10
  conversationQuality: number; // 1-10
  physicalChemistry: number; // 1-10
  greenFlags: string[];
  redFlags: string[];
  notes: string;
  wouldSeeAgain: 'yes' | 'maybe' | 'no';
  createdAt: string;
}

export default function DateJournal() {
  const navigate = useNavigate();
  const [view, setView] = useState<'list' | 'add' | 'detail'>('list');
  const [entries, setEntries] = useState<DateEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<DateEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPerson, setFilterPerson] = useState('all');

  // Form state
  const [personName, setPersonName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [location, setLocation] = useState('');
  const [activityType, setActivityType] = useState('');
  const [duration, setDuration] = useState(2);
  const [emotionalState, setEmotionalState] = useState<'great' | 'good' | 'neutral' | 'mixed' | 'disappointed'>('good');
  const [connectionLevel, setConnectionLevel] = useState(5);
  const [conversationQuality, setConversationQuality] = useState(5);
  const [physicalChemistry, setPhysicalChemistry] = useState(5);
  const [greenFlags, setGreenFlags] = useState<string[]>([]);
  const [redFlags, setRedFlags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [wouldSeeAgain, setWouldSeeAgain] = useState<'yes' | 'maybe' | 'no'>('yes');

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = () => {
    const stored = localStorage.getItem('tension_date_journal');
    if (stored) {
      setEntries(JSON.parse(stored));
    }
  };

  const saveEntry = () => {
    if (!personName.trim()) {
      toast.error('Please enter their name');
      return;
    }

    if (!date) {
      toast.error('Please select a date');
      return;
    }

    const newEntry: DateEntry = {
      id: selectedEntry?.id || `date_${Date.now()}`,
      personName: personName.trim(),
      date,
      location: location.trim(),
      activityType: activityType.trim(),
      duration,
      emotionalState,
      connectionLevel,
      conversationQuality,
      physicalChemistry,
      greenFlags: greenFlags.filter(f => f.trim()),
      redFlags: redFlags.filter(f => f.trim()),
      notes: notes.trim(),
      wouldSeeAgain,
      createdAt: selectedEntry?.createdAt || new Date().toISOString(),
    };

    let updatedEntries;
    if (selectedEntry) {
      // Update existing entry
      updatedEntries = entries.map(e => e.id === selectedEntry.id ? newEntry : e);
      toast.success('Date entry updated!');
    } else {
      // Add new entry
      updatedEntries = [...entries, newEntry];
      toast.success('Date entry saved!');
    }

    localStorage.setItem('tension_date_journal', JSON.stringify(updatedEntries));
    setEntries(updatedEntries);
    resetForm();
    setView('list');
  };

  const deleteEntry = (id: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    const updatedEntries = entries.filter(e => e.id !== id);
    localStorage.setItem('tension_date_journal', JSON.stringify(updatedEntries));
    setEntries(updatedEntries);
    setView('list');
    toast.success('Entry deleted');
  };

  const editEntry = (entry: DateEntry) => {
    setSelectedEntry(entry);
    setPersonName(entry.personName);
    setDate(entry.date);
    setLocation(entry.location);
    setActivityType(entry.activityType);
    setDuration(entry.duration);
    setEmotionalState(entry.emotionalState);
    setConnectionLevel(entry.connectionLevel);
    setConversationQuality(entry.conversationQuality);
    setPhysicalChemistry(entry.physicalChemistry);
    setGreenFlags(entry.greenFlags);
    setRedFlags(entry.redFlags);
    setNotes(entry.notes);
    setWouldSeeAgain(entry.wouldSeeAgain);
    setView('add');
  };

  const viewDetail = (entry: DateEntry) => {
    setSelectedEntry(entry);
    setView('detail');
  };

  const resetForm = () => {
    setSelectedEntry(null);
    setPersonName('');
    setDate(new Date().toISOString().split('T')[0]);
    setLocation('');
    setActivityType('');
    setDuration(2);
    setEmotionalState('good');
    setConnectionLevel(5);
    setConversationQuality(5);
    setPhysicalChemistry(5);
    setGreenFlags([]);
    setRedFlags([]);
    setNotes('');
    setWouldSeeAgain('yes');
  };

  const getEmotionalIcon = (state: string) => {
    switch (state) {
      case 'great': return <Smile className="w-5 h-5 text-green-600" />;
      case 'good': return <Smile className="w-5 h-5 text-blue-600" />;
      case 'neutral': return <Meh className="w-5 h-5 text-neutral-600" />;
      case 'mixed': return <Meh className="w-5 h-5 text-yellow-600" />;
      case 'disappointed': return <Frown className="w-5 h-5 text-red-600" />;
      default: return <Meh className="w-5 h-5" />;
    }
  };

  const getEmotionalColor = (state: string) => {
    switch (state) {
      case 'great': return 'bg-green-100 text-green-800 border-green-200';
      case 'good': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'neutral': return 'bg-neutral-100 text-neutral-800 border-neutral-200';
      case 'mixed': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'disappointed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  const filteredEntries = entries
    .filter(e => {
      if (filterPerson !== 'all' && e.personName !== filterPerson) return false;
      if (searchQuery && !e.personName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const uniquePeople = Array.from(new Set(entries.map(e => e.personName)));

  const getStats = () => {
    if (entries.length === 0) return null;

    const avgConnection = entries.reduce((sum, e) => sum + e.connectionLevel, 0) / entries.length;
    const avgConversation = entries.reduce((sum, e) => sum + e.conversationQuality, 0) / entries.length;
    const totalDates = entries.length;
    const wouldSeeAgainCount = entries.filter(e => e.wouldSeeAgain === 'yes').length;

    return {
      avgConnection: avgConnection.toFixed(1),
      avgConversation: avgConversation.toFixed(1),
      totalDates,
      successRate: Math.round((wouldSeeAgainCount / totalDates) * 100),
    };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (view === 'list') {
                  navigate(-1);
                } else {
                  setView('list');
                  resetForm();
                }
              }}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <div>
              <h1 className="text-xl font-light">Connection Journal</h1>
              <p className="text-sm text-neutral-600">
                {view === 'list' && `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
                {view === 'add' && (selectedEntry ? 'Edit Entry' : 'New Entry')}
                {view === 'detail' && 'Entry Details'}
              </p>
            </div>
          </div>

          {view === 'list' && (
            <Button
              onClick={() => setView('add')}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* List View */}
        {view === 'list' && (
          <div className="space-y-6">
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-violet-600">{stats.totalDates}</div>
                  <div className="text-xs text-neutral-600 mt-1">Total Entries</div>
                </Card>
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.avgConnection}</div>
                  <div className="text-xs text-neutral-600 mt-1">Avg Connection</div>
                </Card>
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.avgConversation}</div>
                  <div className="text-xs text-neutral-600 mt-1">Avg Conversation</div>
                </Card>
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{stats.successRate}%</div>
                  <div className="text-xs text-neutral-600 mt-1">Would See Again</div>
                </Card>
              </div>
            )}

            {/* Filters */}
            {entries.length > 0 && (
              <Card className="p-4">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name..."
                      className="pl-9"
                    />
                  </div>
                  
                  {uniquePeople.length > 1 && (
                    <select
                      value={filterPerson}
                      onChange={(e) => setFilterPerson(e.target.value)}
                      className="px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                    >
                      <option value="all">All People</option>
                      {uniquePeople.map(person => (
                        <option key={person} value={person}>{person}</option>
                      ))}
                    </select>
                  )}
                </div>
              </Card>
            )}

            {/* Entries */}
            {filteredEntries.length === 0 ? (
              <Card className="p-12 text-center">
                <Calendar className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="font-medium text-neutral-900 mb-2">No journal entries yet</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Start tracking your connection journey to identify patterns and growth
                </p>
                <Button onClick={() => setView('add')} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Record Your First Entry
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredEntries.map(entry => (
                  <Card key={entry.id} className="p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-neutral-900">{entry.personName}</h3>
                          <div className="flex items-center gap-2 text-sm text-neutral-600 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(entry.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                            {entry.location && (
                              <>
                                <span>•</span>
                                <MapPin className="w-3 h-3" />
                                {entry.location}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className={`px-3 py-1 rounded-full flex items-center gap-2 ${getEmotionalColor(entry.emotionalState)}`}>
                        {getEmotionalIcon(entry.emotionalState)}
                        <span className="text-sm font-medium capitalize">{entry.emotionalState}</span>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-neutral-600 mb-1">Connection</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-violet-600 rounded-full"
                              style={{ width: `${entry.connectionLevel * 10}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-neutral-900">{entry.connectionLevel}/10</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-600 mb-1">Conversation</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-600 rounded-full"
                              style={{ width: `${entry.conversationQuality * 10}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-neutral-900">{entry.conversationQuality}/10</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-600 mb-1">Chemistry</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-pink-600 rounded-full"
                              style={{ width: `${entry.physicalChemistry * 10}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-neutral-900">{entry.physicalChemistry}/10</span>
                        </div>
                      </div>
                    </div>

                    {/* Flags */}
                    {(entry.greenFlags.length > 0 || entry.redFlags.length > 0) && (
                      <div className="flex gap-2 mb-4">
                        {entry.greenFlags.length > 0 && (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {entry.greenFlags.length} green flag{entry.greenFlags.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        {entry.redFlags.length > 0 && (
                          <Badge className="bg-red-100 text-red-800 border-red-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {entry.redFlags.length} red flag{entry.redFlags.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-neutral-200">
                      <Button
                        onClick={() => viewDetail(entry)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => editEntry(entry)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => deleteEntry(entry.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add/Edit View */}
        {view === 'add' && (
          <div className="space-y-6">
            {/* Basic Info */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium">Interaction Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Their Name *
                  </label>
                  <Input
                    value={personName}
                    onChange={(e) => setPersonName(e.target.value)}
                    placeholder="e.g., Alex"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Entry Date *
                  </label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Location
                  </label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Downtown Cafe"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Activity Type
                  </label>
                  <Input
                    value={activityType}
                    onChange={(e) => setActivityType(e.target.value)}
                    placeholder="e.g., Coffee, Dinner, Activity"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-700 block mb-2">
                  Duration: {duration} hour{duration !== 1 ? 's' : ''}
                </label>
                <Slider
                  value={[duration]}
                  onValueChange={(v) => setDuration(v[0])}
                  min={0.5}
                  max={8}
                  step={0.5}
                />
              </div>
            </Card>

            {/* Emotional State */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium">How did you feel?</h2>
              
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'great', label: 'Great', icon: Smile },
                  { key: 'good', label: 'Good', icon: Smile },
                  { key: 'neutral', label: 'Neutral', icon: Meh },
                  { key: 'mixed', label: 'Mixed', icon: Meh },
                  { key: 'disappointed', label: 'Disappointed', icon: Frown },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setEmotionalState(key as any)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      emotionalState === key
                        ? 'border-violet-600 bg-violet-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Rating Scales */}
            <Card className="p-6 space-y-6">
              <h2 className="font-medium">Rate the Experience</h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Emotional Connection
                    </label>
                    <span className="text-sm font-medium text-violet-600">{connectionLevel}/10</span>
                  </div>
                  <Slider
                    value={[connectionLevel]}
                    onValueChange={(v) => setConnectionLevel(v[0])}
                    min={1}
                    max={10}
                    step={1}
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Conversation Quality
                    </label>
                    <span className="text-sm font-medium text-blue-600">{conversationQuality}/10</span>
                  </div>
                  <Slider
                    value={[conversationQuality]}
                    onValueChange={(v) => setConversationQuality(v[0])}
                    min={1}
                    max={10}
                    step={1}
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-neutral-700">
                      Physical Chemistry
                    </label>
                    <span className="text-sm font-medium text-pink-600">{physicalChemistry}/10</span>
                  </div>
                  <Slider
                    value={[physicalChemistry]}
                    onValueChange={(v) => setPhysicalChemistry(v[0])}
                    min={1}
                    max={10}
                    step={1}
                  />
                </div>
              </div>
            </Card>

            {/* Flags */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium">Green & Red Flags</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-green-700 block mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Green Flags Observed
                  </label>
                  {greenFlags.map((flag, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <Input
                        value={flag}
                        onChange={(e) => {
                          const newFlags = [...greenFlags];
                          newFlags[idx] = e.target.value;
                          setGreenFlags(newFlags);
                        }}
                        placeholder="e.g., Asked thoughtful questions"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setGreenFlags(greenFlags.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGreenFlags([...greenFlags, ''])}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Green Flag
                  </Button>
                </div>

                <div>
                  <label className="text-sm font-medium text-red-700 block mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Red Flags Observed
                  </label>
                  {redFlags.map((flag, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <Input
                        value={flag}
                        onChange={(e) => {
                          const newFlags = [...redFlags];
                          newFlags[idx] = e.target.value;
                          setRedFlags(newFlags);
                        }}
                        placeholder="e.g., Talked only about themselves"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRedFlags(redFlags.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRedFlags([...redFlags, ''])}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Red Flag
                  </Button>
                </div>
              </div>
            </Card>

            {/* Notes */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium">Notes & Reflections</h2>
              
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                    placeholder="How did the interaction go? Any memorable moments, concerns, or excitement about seeing them again?"
                className="min-h-[120px]"
              />
            </Card>

            {/* Would See Again */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium">Would you see them again?</h2>
              
              <div className="flex gap-3">
                {[
                  { key: 'yes', label: 'Yes, definitely', color: 'green' },
                  { key: 'maybe', label: 'Maybe, unsure', color: 'yellow' },
                  { key: 'no', label: 'No, not interested', color: 'red' },
                ].map(({ key, label, color }) => (
                  <button
                    key={key}
                    onClick={() => setWouldSeeAgain(key as any)}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      wouldSeeAgain === key
                        ? `border-${color}-600 bg-${color}-50`
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setView('list');
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEntry}
                className="flex-1 bg-violet-600 hover:bg-violet-700"
              >
                {selectedEntry ? 'Update Entry' : 'Save Entry'}
              </Button>
            </div>
          </div>
        )}

        {/* Detail View */}
        {view === 'detail' && selectedEntry && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-medium">{selectedEntry.personName}</h2>
                    <div className="flex items-center gap-2 text-sm text-neutral-600 mt-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(selectedEntry.date).toLocaleDateString('en-US', { 
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </div>
                  </div>
                </div>

                <div className={`px-3 py-2 rounded-full flex items-center gap-2 ${getEmotionalColor(selectedEntry.emotionalState)}`}>
                  {getEmotionalIcon(selectedEntry.emotionalState)}
                  <span className="font-medium capitalize">{selectedEntry.emotionalState}</span>
                </div>
              </div>

              {(selectedEntry.location || selectedEntry.activityType) && (
                <div className="grid md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-neutral-200">
                  {selectedEntry.location && (
                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                      <MapPin className="w-4 h-4 text-neutral-500" />
                      <span>{selectedEntry.location}</span>
                    </div>
                  )}
                  {selectedEntry.activityType && (
                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                      <Heart className="w-4 h-4 text-neutral-500" />
                      <span>{selectedEntry.activityType}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <Clock className="w-4 h-4 text-neutral-500" />
                    <span>{selectedEntry.duration} hour{selectedEntry.duration !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}

              <div className="space-y-4 mb-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">Emotional Connection</span>
                    <span className="text-sm font-medium text-violet-600">{selectedEntry.connectionLevel}/10</span>
                  </div>
                  <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-violet-600 rounded-full"
                      style={{ width: `${selectedEntry.connectionLevel * 10}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">Conversation Quality</span>
                    <span className="text-sm font-medium text-blue-600">{selectedEntry.conversationQuality}/10</span>
                  </div>
                  <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-600 rounded-full"
                      style={{ width: `${selectedEntry.conversationQuality * 10}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">Physical Chemistry</span>
                    <span className="text-sm font-medium text-pink-600">{selectedEntry.physicalChemistry}/10</span>
                  </div>
                  <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-pink-600 rounded-full"
                      style={{ width: `${selectedEntry.physicalChemistry * 10}%` }}
                    />
                  </div>
                </div>
              </div>

              {selectedEntry.greenFlags.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-green-700 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Green Flags
                  </h3>
                  <ul className="space-y-2">
                    {selectedEntry.greenFlags.map((flag, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-green-900 bg-green-50 p-3 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{flag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedEntry.redFlags.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-red-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Red Flags
                  </h3>
                  <ul className="space-y-2">
                    {selectedEntry.redFlags.map((flag, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-red-900 bg-red-50 p-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{flag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedEntry.notes && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-neutral-700 mb-2">Notes & Reflections</h3>
                  <p className="text-sm text-neutral-700 whitespace-pre-wrap bg-neutral-50 p-4 rounded-lg">
                    {selectedEntry.notes}
                  </p>
                </div>
              )}

              <div className="pt-6 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">Would see them again?</span>
                  <Badge className={
                    selectedEntry.wouldSeeAgain === 'yes' 
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : selectedEntry.wouldSeeAgain === 'maybe'
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                      : 'bg-red-100 text-red-800 border-red-200'
                  }>
                    {selectedEntry.wouldSeeAgain === 'yes' && 'Yes, definitely'}
                    {selectedEntry.wouldSeeAgain === 'maybe' && 'Maybe, unsure'}
                    {selectedEntry.wouldSeeAgain === 'no' && 'No, not interested'}
                  </Badge>
                </div>
              </div>
            </Card>

            <div className="flex gap-3">
              <Button
                onClick={() => setView('list')}
                variant="outline"
                className="flex-1"
              >
                Back to List
              </Button>
              <Button
                onClick={() => editEntry(selectedEntry)}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Entry
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
